import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, fetchWithCredentials } from '../config/api';
import './OrderPage.css'; // Assuming you'll create a CSS file for this page

function OrderPage() {
  const { isLoggedIn } = useAuth();
  const [orders, setOrders] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState({});
  const [resendingEmail, setResendingEmail] = useState({});

  useEffect(() => {
    console.log('OrderPage mounted, isLoggedIn:', isLoggedIn);

    const fetchOrders = async () => {
      if (!isLoggedIn) {
        console.log('User not logged in, showing login message');
        setLoading(false);
        setError('请先登录以查看您的订单');
        return;
      }

      try {
        console.log('Fetching orders...');
        const response = await fetchWithCredentials(API_ENDPOINTS.ORDERS.MY, { method: 'GET' });
        console.log('Orders response:', response);
        setOrders(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('获取订单失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isLoggedIn]); // Refetch orders if login status changes

  const handleSimulatePayment = async (orderId) => {
    setProcessingPayment(prev => ({ ...prev, [orderId]: true }));
    
    try {
      console.log('开始模拟支付，订单ID:', orderId);
      console.log('请求URL:', `${API_ENDPOINTS.ORDERS.BASE}/${orderId}/simulate-payment`);
      
      const response = await fetchWithCredentials(
        `${API_ENDPOINTS.ORDERS.BASE}/${orderId}/simulate-payment`,
        { method: 'POST' }
      );
      
      console.log('模拟支付响应:', response);
      
      if (response.message) {
        // 刷新订单列表
        const updatedOrders = await fetchWithCredentials(API_ENDPOINTS.ORDERS.MY, { method: 'GET' });
        setOrders(Array.isArray(updatedOrders) ? updatedOrders : []);
        alert('模拟支付成功！请查看您的邮箱获取序列号。');
      }
    } catch (err) {
      console.error('模拟支付失败:', err);
      console.error('错误详情:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      
      // 显示更详细的错误信息
      let errorMessage = '模拟支付失败，请稍后重试';
      if (err.message.includes('登录已过期')) {
        errorMessage = '登录已过期，请重新登录';
      } else if (err.message.includes('网络')) {
        errorMessage = '网络连接失败，请检查网络连接';
      } else if (err.message.includes('404')) {
        errorMessage = 'API接口不存在，请检查服务器状态';
      } else if (err.message.includes('500')) {
        errorMessage = '服务器内部错误，请联系管理员';
      }
      
      alert(errorMessage);
    } finally {
      setProcessingPayment(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleResendEmail = async (orderId) => {
    setResendingEmail(prev => ({ ...prev, [orderId]: true }));
    
    try {
      console.log('开始重发邮件，订单ID:', orderId);
      
      const response = await fetchWithCredentials(
        `${API_ENDPOINTS.ORDERS.BASE}/${orderId}/resend-email`,
        { method: 'POST' }
      );
      
      console.log('重发邮件响应:', response);
      
      if (response.message) {
        alert(`邮件重发成功！已发送到 ${response.email}`);
      }
    } catch (err) {
      console.error('重发邮件失败:', err);
      
      let errorMessage = '重发邮件失败，请稍后重试';
      if (err.message.includes('登录已过期')) {
        errorMessage = '登录已过期，请重新登录';
      } else if (err.message.includes('404')) {
        errorMessage = '订单不存在或未支付完成';
      } else if (err.message.includes('邮件发送失败')) {
        errorMessage = '邮件发送失败，请检查邮箱配置';
      }
      
      alert(errorMessage);
    } finally {
      setResendingEmail(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusText = (paymentStatus) => {
    switch (paymentStatus) {
      case 'completed':
        return '已支付';
      case 'pending':
        return '待支付';
      case 'failed':
        return '支付失败';
      default:
        return '未知状态';
    }
  };

  const getStatusClass = (paymentStatus) => {
    switch (paymentStatus) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-failed';
      default:
        return 'status-unknown';
    }
  };

  if (loading) {
    return <div className="loading">加载订单中...</div>;
  }

  if (error) {
    return (
      <div className="order-page">
        <div className="container">
          <h1>我的订单</h1>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      <div className="container">
        <h1>我的订单</h1>

        {!orders || orders.length === 0 ? (
          <div className="no-orders">
            <p>您还没有任何订单记录</p>
            <p className="sub-text">购买套餐后，您的订单将显示在这里</p>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => {
              const usdtAmount = order.usdt_actual_amount || parseFloat(order.amount);
              return (
                <div key={order.id} className="order-item">
                  <div className="order-header">
                    <h3>订单号: {order.orderNumber}</h3>
                    <span className={`status ${getStatusClass(order.paymentStatus)}`}>
                      {getStatusText(order.paymentStatus)}
                    </span>
                  </div>
                  
                  <div className="order-content">
                    <div className="order-details">
                      <div className="detail-row">
                        <span className="label">金额:</span>
                        <span className="value">{usdtAmount} {order.currency}</span>
                      </div>
                      
                      {order.package && (
                        <div className="detail-row">
                          <span className="label">套餐:</span>
                          <span className="value">{order.package.name}</span>
                        </div>
                      )}
                      
                      <div className="detail-row">
                        <span className="label">购买日期:</span>
                        <span className="value">{new Date(order.createdAt).toLocaleDateString('zh-CN')}</span>
                      </div>
                      
                      {order.paidAt && (
                        <div className="detail-row">
                          <span className="label">支付时间:</span>
                          <span className="value">{new Date(order.paidAt).toLocaleDateString('zh-CN')}</span>
                        </div>
                      )}
                    </div>

                    {/* 序列号信息 - 右半部分 */}
                    {order.serialNumber && (
                      <div className="serial-number-section">
                        <h4>授权码信息</h4>
                        <div className="serial-number">
                          <span className="label">序列号:</span>
                          <span className="serial-key">{order.serialNumber.serialKey}</span>
                        </div>
                        <div className="serial-details">
                          <div className="detail-row">
                            <span className="label">类型:</span>
                            <span className="value">{order.serialNumber.type} 个月</span>
                          </div>
                          {order.serialNumber.startDate && (
                            <div className="detail-row">
                              <span className="label">生效时间:</span>
                              <span className="value">{new Date(order.serialNumber.startDate).toLocaleDateString('zh-CN')}</span>
                            </div>
                          )}
                          {order.serialNumber.endDate && (
                            <div className="detail-row">
                              <span className="label">到期时间:</span>
                              <span className="value">{new Date(order.serialNumber.endDate).toLocaleDateString('zh-CN')}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* 重发邮件按钮 */}
                        <div className="email-actions">
                          <button 
                            className="resend-email-btn"
                            onClick={() => handleResendEmail(order.id)}
                            disabled={resendingEmail[order.id]}
                          >
                            {resendingEmail[order.id] ? '发送中...' : '📧 重发邮件'}
                          </button>
                          <p className="resend-note">* 重新发送包含序列号的订阅邮件</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 模拟支付按钮 */}
                  {process.env.NODE_ENV === 'development' && order.paymentStatus === 'pending' && (
                    <div className="payment-actions">
                      <button 
                        className="simulate-payment-btn"
                        onClick={() => handleSimulatePayment(order.id)}
                        disabled={processingPayment[order.id]}
                      >
                        {processingPayment[order.id] ? '处理中...' : '模拟支付成功'}
                      </button>
                      <p className="simulate-note">* 此按钮仅用于测试，模拟支付成功流程</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderPage; 